import { useContext, useState } from 'react'

import { type SanityProductFragment } from '@data/sanity/queries/types/product'
import { type AttributeInput } from '@data/shopify/storefront/types'
import { AnalyticsEventName } from '@lib/analytics'
import { AnalyticsContext } from '@lib/analytics-context'
import { useAddItemsToCart } from '@lib/cart/hooks'
import { StringsContext } from '@lib/strings-context'

import Alert from '@components/alert'
import { ButtonVariant } from '@components/buttons/button'
import SimplePortableText from '@components/simple-portable-text'
import ProductAddToCart from './product-add-to-cart'
import ProductCounter from './product-counter'
import ProductWaitlist from './product-waitlist'

interface ProductActionsProps {
  product: SanityProductFragment
  variantIds: number[]
  inStock: boolean
  cartAttributes?: AttributeInput[]
}

const ProductActions = ({
  product,
  variantIds,
  inStock,
  cartAttributes,
}: ProductActionsProps) => {
  const strings = useContext(StringsContext)
  const { triggerAnalyticsEvent } = useContext(AnalyticsContext)

  const [quantity, setQuantity] = useState(1)
  const [isAddToCartError, setIsAddToCartError] = useState(false)

  const addItemsToCart = useAddItemsToCart()

  const handleAddToCart = async () => {
    setIsAddToCartError(false)

    const isSuccessful = await addItemsToCart(
      variantIds.map((variantId) => ({
        id: variantId,
        quantity,
        productId: product._id,
        productCategoryId: product.productCategoryId,
        attributes: cartAttributes,
      }))
    )

    setIsAddToCartError(!isSuccessful)

    if (isSuccessful) {
      triggerAnalyticsEvent(AnalyticsEventName.AddToCart)
    }
  }

  if (!inStock && variantIds.length === 1) {
    return (
      <ProductWaitlist
        // TODO: Move to product hero component setting.
        service="klaviyo"
        variantId={variantIds[0]}
      />
    )
  }

  return (
    <div className="flex flex-col gap-y-5 items-baseline w-full text-center">
      <ProductCounter max={10} onUpdate={setQuantity} />

      <ProductAddToCart
        variant={ButtonVariant.FILLED}
        disabled={variantIds.length === 0}
        onClick={handleAddToCart}
        className="max-w-sm w-full"
      >
        {strings.buttonAddToCart}
      </ProductAddToCart>

      {isAddToCartError && (
        <div key="error">
          <Alert error>
            <SimplePortableText
              className="rc rc-alert rc-error"
              content={strings.cartAddToCartErrorMessage}
            />
          </Alert>
        </div>
      )}
    </div>
  )
}

export default ProductActions
